// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
/**
 * Date utility functions for consistent American date formatting
 * All dates are formatted as MM/DD/YYYY in American format
 * 
 * IMPORTANT: These functions handle timezone-agnostic date parsing to prevent
 * dates from shifting due to timezone conversions. For example, "2025-12-15"
 * will always display as December 15, 2025, regardless of user timezone.
 */

const AMERICAN_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
}

/**
 * Parse a date string in a timezone-agnostic way
 * Prevents dates from shifting due to timezone conversions
 * @param dateString - Date string in YYYY-MM-DD or ISO format
 * @returns Date object representing local date/time
 */
function parseTimezoneAgnostic(dateString: string): Date {
  // Handle ISO datetime strings (YYYY-MM-DDTHH:mm:ss.sssZ)
  if (dateString.includes('T')) {
    // For datetime strings, use standard Date constructor
    return new Date(dateString)
  }
  
  // For date-only strings (YYYY-MM-DD), parse components to avoid timezone shift
  const parts = dateString.split('-')
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10)
    const month = parseInt(parts[1], 10) - 1 // Month is 0-indexed
    const day = parseInt(parts[2], 10)
    return new Date(year, month, day)
  }
  
  // Fallback to standard parsing
  return new Date(dateString)
}

/**
 * Get a date in YYYY-MM-DD format using LOCAL timezone (not UTC).
 * Prevents timezone-shift bugs from `toISOString()` when the user's TZ
 * isn't UTC.
 *
 * @example
 * getLocalDateString()                                  // "2026-05-07"
 * getLocalDateString(new Date('2026-12-31T23:00:00-05:00')) // "2026-12-31"
 */
export function getLocalDateString(date: Date = new Date()): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Get date N days from now in YYYY-MM-DD format using LOCAL timezone.
 * Pass a negative number for past dates.
 */
export function getLocalDatePlusDays(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() + days)
  return getLocalDateString(date)
}

/**
 * Add N days to a GIVEN date string and return YYYY-MM-DD, timezone-agnostic.
 * Unlike {@link getLocalDatePlusDays} (which counts from "now"), this counts
 * from the passed date — so it can derive e.g. an invoice's due date from its
 * chosen issue date regardless of what "today" is.
 *
 * The input is parsed the same timezone-agnostic way as {@link formatDate}, so
 * a date-only string never shifts a day under a non-UTC timezone. A blank or
 * unparseable input returns `''` (nothing to derive from), letting callers
 * leave the field untouched rather than emit an "Invalid Date".
 *
 * @example
 * addDaysToDateString('2026-09-01', 30) // "2026-10-01"  (month rolls over)
 * addDaysToDateString('2026-02-15', 30) // "2026-03-17"  (leap-year Feb)
 * addDaysToDateString('', 30)           // ""
 */
export function addDaysToDateString(dateString: string, days: number): string {
  if (!dateString) return ''
  const parsed = parseTimezoneAgnostic(dateString)
  if (Number.isNaN(parsed.getTime())) return ''
  // Rebuild from the parsed calendar parts so the result is a pure local date
  // (no time-of-day carried over from an ISO input), then add the days.
  const local = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
  local.setDate(local.getDate() + days)
  return getLocalDateString(local)
}

/**
 * Format a date string or Date object to American format (MM/DD/YYYY)
 * @param date - Date string or Date object
 * @returns Formatted date string in MM/DD/YYYY format
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '-'

  try {
    const dateObj = typeof date === 'string' ? parseTimezoneAgnostic(date) : date
    return dateObj.toLocaleDateString('en-US', AMERICAN_DATE_OPTIONS)
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'Invalid Date'
  }
}

/**
 * Compose the invoice "Service Period" display string from an optional
 * from/to pair. Both set → "MM/DD/YYYY — MM/DD/YYYY"; only one set → that
 * single formatted date; neither → '' (an empty string, so callers can
 * skip rendering the row entirely — note this differs from `formatDate`,
 * which returns '-' for an empty date).
 *
 * Kept here next to `formatDate` so the on-screen invoice detail dialog
 * renders the period consistently with the app's other dates. (The PDF
 * keeps its own long-form date formatter, so it composes the period
 * inline rather than reusing this.)
 */
export function formatServicePeriod(
  from: string | Date | null | undefined,
  to: string | Date | null | undefined,
): string {
  const fromStr = from ? formatDate(from) : ''
  const toStr = to ? formatDate(to) : ''
  if (fromStr && toStr) return `${fromStr} — ${toStr}`
  return fromStr || toStr
}

